import { useEffect, useRef, useState } from 'react';
import { Check, CircleDashed, Copy, ExternalLink, LoaderCircle, X } from '@plataforma/ui/iconos';
import { useApi } from '@plataforma/web/api';

export interface Dominio {
  id: string;
  dominio: string;
  tipo: 'subdominio' | 'propio';
  verificado: boolean;
  certificado: string | null;
  instrucciones: {
    cname: { nombre: string; tipo: string; valor: string };
    txt: { nombre: string; tipo: string; valor: string };
    nota: string;
  } | null;
  revision?: { txt: boolean; apunta: boolean } | null;
}

type Estado = 'hecho' | 'actual' | 'espera' | 'error';

/** Cada cuánto se vuelve a comprobar mientras la guía está abierta, y cuántas veces. */
const INTERVALO_MS = 20_000;
const MAX_INTENTOS = 45;

function Paso({
  n,
  estado,
  titulo,
  children,
}: {
  n: number;
  estado: Estado;
  titulo: string;
  children?: React.ReactNode;
}) {
  const Icono =
    estado === 'hecho'
      ? Check
      : estado === 'error'
        ? X
        : estado === 'actual'
          ? LoaderCircle
          : CircleDashed;
  return (
    <li className={`paso-guia paso-${estado}`}>
      <span className="paso-marca" aria-hidden="true">
        {estado === 'espera' ? n : <Icono size={16} strokeWidth={2.25} />}
      </span>
      <div>
        <h3>{titulo}</h3>
        {children}
      </div>
    </li>
  );
}

function Copiable({ texto }: { texto: string }) {
  const [copiado, setCopiado] = useState(false);
  return (
    <button
      type="button"
      className="boton chico"
      onClick={() =>
        void navigator.clipboard?.writeText(texto).then(() => {
          setCopiado(true);
          setTimeout(() => setCopiado(false), 1500);
        })
      }
      aria-label={`Copiar ${texto}`}
    >
      {copiado ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
      {copiado ? 'Copiado' : 'Copiar'}
    </button>
  );
}

const certificadoListo = (c: string | null) => c === 'activo';
const certificadoNoAplica = (c: string | null) => !!c && c.startsWith('no aplica');
const certificadoError = (c: string | null) =>
  !!c && (c.startsWith('error') || c.includes('no encontrado'));

/**
 * Guía de dominio propio (arquitectura 2.16): 1) agregado, 2) registros por copiar, 3) comprobación
 * en vivo del DNS y 4) estado del certificado. Mientras la guía está abierta se vuelve a comprobar
 * sola hasta que el dominio quede verificado y con certificado.
 */
export function GuiaDominio({ d, alCambiar }: { d: Dominio; alCambiar: () => void }) {
  const api = useApi();
  const [revision, setRevision] = useState(d.revision ?? null);
  const [comprobando, setComprobando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const intentos = useRef(0);
  const terminado =
    d.verificado && (certificadoListo(d.certificado) || certificadoNoAplica(d.certificado));

  const comprobar = async () => {
    setComprobando(true);
    setError(null);
    try {
      const r = await api<Dominio>('POST', `/platform/mi-lotero/dominios/${d.id}/verificar`);
      setRevision(r.revision ?? null);
      if (r.verificado !== d.verificado || r.certificado !== d.certificado) alCambiar();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setComprobando(false);
    }
  };

  useEffect(() => {
    if (terminado) return;
    const reloj = setInterval(() => {
      if (intentos.current >= MAX_INTENTOS || document.hidden) return;
      intentos.current += 1;
      void comprobar();
    }, INTERVALO_MS);
    return () => clearInterval(reloj);
  }, [d.id, d.verificado, d.certificado, terminado]);

  const registros = d.instrucciones ? [d.instrucciones.cname, d.instrucciones.txt] : [];
  const dnsListo = d.verificado || (revision?.txt && revision.apunta);
  const estadoCert: Estado = certificadoListo(d.certificado)
    ? 'hecho'
    : certificadoError(d.certificado)
      ? 'error'
      : d.verificado
        ? 'actual'
        : 'espera';

  return (
    <ol className="guia-dominio">
      <Paso n={1} estado="hecho" titulo={`Agregaste ${d.dominio}`} />
      <Paso
        n={2}
        estado={dnsListo ? 'hecho' : 'actual'}
        titulo="Copia estos registros en el DNS de tu dominio"
      >
        {!dnsListo && (
          <>
            <p className="subtitulo">
              Entra al panel donde compraste tu dominio (GoDaddy, Google, Cloudflare, Hostinger…),
              busca «DNS» o «Zona DNS» y agrega los dos registros tal cual.
            </p>
            <div className="registros-dns">
              {registros.map((x) => (
                <div key={x.tipo} className="registro-dns">
                  <span className="etiqueta">{x.tipo}</span>
                  <dl>
                    <dt>Nombre</dt>
                    <dd className="vin">{x.nombre}</dd>
                    <dt>Valor</dt>
                    <dd className="vin">{x.valor}</dd>
                  </dl>
                  <div className="acciones">
                    <Copiable texto={x.nombre} />
                    <Copiable texto={x.valor} />
                  </div>
                </div>
              ))}
            </div>
            {d.instrucciones?.nota && <p className="subtitulo">{d.instrucciones.nota}</p>}
          </>
        )}
      </Paso>
      <Paso
        n={3}
        estado={d.verificado ? 'hecho' : dnsListo ? 'actual' : revision ? 'actual' : 'espera'}
        titulo={d.verificado ? 'Tu dominio está verificado' : 'Comprobamos tus registros'}
      >
        {!d.verificado && (
          <>
            <ul className="comprobacion-dns">
              <li className={revision?.txt ? 'si' : 'no'}>
                {revision?.txt ? (
                  <Check size={14} aria-hidden="true" />
                ) : (
                  <X size={14} aria-hidden="true" />
                )}{' '}
                {revision?.txt ? 'Encontramos el registro TXT' : 'Aún no vemos el registro TXT'}
              </li>
              <li className={revision?.apunta ? 'si' : 'no'}>
                {revision?.apunta ? (
                  <Check size={14} aria-hidden="true" />
                ) : (
                  <X size={14} aria-hidden="true" />
                )}{' '}
                {revision?.apunta
                  ? 'Tu dominio ya apunta a la tienda'
                  : 'Tu dominio aún no apunta a la tienda'}
              </li>
            </ul>
            <p className="subtitulo">
              El DNS puede tardar desde unos minutos hasta unas horas. Mientras esta página esté
              abierta lo revisamos solos cada 20 segundos.
            </p>
            <button
              className="boton primario"
              onClick={() => void comprobar()}
              disabled={comprobando}
            >
              {comprobando ? 'Comprobando…' : 'Comprobar ahora'}
            </button>
          </>
        )}
      </Paso>
      <Paso
        n={4}
        estado={certificadoNoAplica(d.certificado) ? 'hecho' : estadoCert}
        titulo={
          certificadoListo(d.certificado)
            ? 'Tu tienda ya abre en tu dominio, con candado'
            : 'Certificado de seguridad (el candado del navegador)'
        }
      >
        {certificadoListo(d.certificado) && (
          <a
            className="boton primario"
            href={`https://${d.dominio}/`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ExternalLink size={16} aria-hidden="true" /> Abrir {d.dominio}
          </a>
        )}
        {d.verificado && estadoCert === 'actual' && !certificadoNoAplica(d.certificado) && (
          <p className="subtitulo">
            Se está emitiendo ({d.certificado}). Suele tardar menos de 15 minutos; no tienes que
            hacer nada.
          </p>
        )}
        {certificadoNoAplica(d.certificado) && (
          <p className="subtitulo">En este ambiente de pruebas no se emiten certificados.</p>
        )}
        {estadoCert === 'error' && (
          <p className="error">
            No se pudo emitir el certificado ({d.certificado}). Escríbenos desde Ayuda y lo
            revisamos.
          </p>
        )}
      </Paso>
      {error && <li className="error">{error}</li>}
    </ol>
  );
}
